import { useState, type FormEvent, type ReactElement } from 'react';
import { apiErrorCode, emberApi } from '../api';
import { trackFailed } from '../../../../../shared-web/track';
import { navigate } from '../router';
import { useRequireAuth } from '../use-require-auth';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { markOpenedThisSession, removeLegacyLock } from '../notebook-lock';
import './NotebookUnlock.css';

/**
 * Where the texted link lands (§4.12, 2026-09-07 拍板): /me/unlock?t=<ticket>.
 *
 * The `/r/` link that carried it has already signed this browser in, so the
 * person is home — what they still cannot do is produce the password. The
 * ticket in the URL is what says they arrived through the door their own phone
 * received: single-use, thirty minutes, and spent the moment they choose a new
 * password or take the lock off. A signed-in browser without one gets neither.
 *
 * A flow page, outside the level ladder: it carries Back and Home, and it
 * replaces itself on the way out, because a door that has been used up must
 * not stay in history for Back to walk into.
 */
export function NotebookUnlockPage({ search }: { search: URLSearchParams }): ReactElement {
  const ticket = String(search.get('t') || '').trim();
  // The ticket rides the returnTo, so a link opened in a browser whose session
  // has since died survives the trip through /auth.
  const status = useRequireAuth(ticket ? `/me/unlock?t=${encodeURIComponent(ticket)}` : '/me/unlock');

  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /** The ticket is gone — used already, or older than its half hour. */
  const [spent, setSpent] = useState(!ticket);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState('');

  /**
   * Both ways out end the same: this browser has just proved itself, so the
   * notebook opens rather than asking again on the next step. The legacy local
   * lock goes too — it is the one thing a reset on the server cannot answer,
   * and leaving it behind would put a cover back over the page they were sent
   * here to open.
   */
  const land = (): void => {
    removeLegacyLock();
    markOpenedThisSession();
    navigate('/me/embers', { replace: true });
  };

  const failure = (err: unknown): void => {
    const code = apiErrorCode(err);
    if (code === 'ticket_invalid') { setSpent(true); return; }
    setError(
      code === 'password_too_short' ? 'A little longer — at least four characters.'
        : 'That didn’t go through. Try again.',
    );
    trackFailed('notebook_reset', code || 'request_failed');
  };

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (busy || !draft.trim()) return;
    setBusy(true);
    setError('');
    emberApi.setNotebookLock({ password: draft, ticket, source: 'reset' })
      .then(land)
      .catch(failure)
      .finally(() => setBusy(false));
  };

  const takeOff = (): void => {
    if (busy) return;
    setBusy(true);
    setError('');
    emberApi.removeNotebookLock({ ticket })
      .then(land)
      .catch(failure)
      .finally(() => setBusy(false));
  };

  const sendAnother = (): void => {
    if (sending) return;
    setSending(true);
    emberApi.recoverNotebookLock()
      .then((res) => setSentTo(res.hint || 'your phone'))
      .catch((err: unknown) => {
        setError(
          apiErrorCode(err) === 'rate_limited'
            ? 'A link is already on its way. Give it a minute before asking for another.'
            : 'That text didn’t go out. Try again in a moment.',
        );
        trackFailed('notebook_recover', apiErrorCode(err) || 'request_failed');
      })
      .finally(() => setSending(false));
  };

  if (status !== 'authenticated') {
    return <div className="ember-container unlock-page" aria-busy={status === 'loading'} />;
  }

  return (
    <div className="ember-container unlock-page">
      <div className="unlock-toolbar">
        <PageBack className="unlock-back" to="/me/embers" />
        <div className="ember-toolbar-actions">
          <PageHome />
        </div>
      </div>

      {spent ? (
        <div className="unlock-card" data-log-seen="notebook_reset_seen" data-log-state="spent">
          <h1 className="unlock-title">That link has been used</h1>
          <p className="unlock-note">
            Each one opens the notebook once, and only for half an hour. We can
            text you another.
          </p>
          {sentTo ? (
            <p className="unlock-note">
              Sent to {sentTo}. Open the link and you can set a new password.
            </p>
          ) : (
            <button
              type="button"
              className="ember-btn ember-btn-primary unlock-submit"
              disabled={sending}
              data-log="notebook_recover"
              data-log-state="reset_page"
              onClick={sendAnother}
            >
              {sending ? 'Sending…' : 'Text me a new link'}
            </button>
          )}
          {error && <p className="unlock-error" role="alert">{error}</p>}
        </div>
      ) : (
        <form className="unlock-card" onSubmit={submit} data-log-seen="notebook_reset_seen" data-log-state="open">
          <h1 className="unlock-title">Set a new password</h1>
          <p className="unlock-note">
            This is the password your notebook asks for. It keeps the page
            closed on a shared phone — your Embers are kept by your account
            either way.
          </p>
          <input
            className="unlock-input"
            type="password"
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setError(''); }}
            placeholder="A password only you know"
            autoComplete="new-password"
            aria-label="New notebook password"
            autoFocus
          />
          {error && <p className="unlock-error" role="alert">{error}</p>}
          <button
            type="submit"
            className="ember-btn ember-btn-primary unlock-submit"
            disabled={busy || !draft.trim()}
            data-log="notebook_lock_set"
            data-log-state="reset"
          >
            Save it
          </button>
          <button
            type="button"
            className="unlock-secondary ember-text-action"
            disabled={busy}
            data-log="notebook_lock_removed"
            data-log-state="reset"
            onClick={takeOff}
          >
            Or take the lock off
          </button>
        </form>
      )}
    </div>
  );
}
